import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import type { ImportOrderSource } from "@/server/platform/db/schema/inventory";

export const IMPORT_ARRIVAL_FALLBACK_LEAD_DAYS: Record<ImportOrderSource, number> =
  {
    taobao: 14,
    "1688": 12,
    pinduoduo: 14,
    other: 14,
  };

export const IMPORT_ORDER_SOURCES_LIST = [
  "taobao",
  "1688",
  "pinduoduo",
  "other",
] as const satisfies readonly ImportOrderSource[];

export type ImportArrivalEstimateBySource = {
  source: ImportOrderSource;
  medianLeadDays: number;
  sampleCount: number;
  isFallback: boolean;
};

export function medianLeadDays(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return Math.round((sorted[mid - 1]! + sorted[mid]!) / 2);
  }
  return sorted[mid]!;
}

export function calendarLeadDays(
  orderedAt: Date,
  expectedArrivalDate: Date,
): number {
  return Math.max(
    0,
    differenceInCalendarDays(expectedArrivalDate, orderedAt),
  );
}

export function buildImportArrivalEstimates(
  samples: Array<{
    source: ImportOrderSource;
    leadDays: number;
  }>,
): ImportArrivalEstimateBySource[] {
  const bySource = new Map<ImportOrderSource, number[]>();
  for (const sample of samples) {
    const list = bySource.get(sample.source) ?? [];
    list.push(sample.leadDays);
    bySource.set(sample.source, list);
  }

  return IMPORT_ORDER_SOURCES_LIST.map((source) => {
    const leads = bySource.get(source) ?? [];
    const median = medianLeadDays(leads);
    if (median != null) {
      return {
        source,
        medianLeadDays: median,
        sampleCount: leads.length,
        isFallback: false,
      };
    }
    return {
      source,
      medianLeadDays: IMPORT_ARRIVAL_FALLBACK_LEAD_DAYS[source],
      sampleCount: 0,
      isFallback: true,
    };
  });
}

export function estimateExpectedArrivalDateInput(
  orderedAtInput: string | null | undefined,
  leadDays: number,
  now: Date = new Date(),
): string {
  const base =
    orderedAtInput && orderedAtInput.trim().length > 0
      ? parseISO(orderedAtInput)
      : now;
  return format(addDays(base, Math.max(0, leadDays)), "yyyy-MM-dd");
}

export function findImportArrivalEstimate(
  estimates: ImportArrivalEstimateBySource[],
  source: ImportOrderSource,
): ImportArrivalEstimateBySource | undefined {
  return estimates.find((row) => row.source === source);
}
